import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Bell, SlidersHorizontal, Heart, Calendar, Star, LogOut, Trash2, Camera, Music, Wine, Store, Compass } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, API } from "@/lib/api";
import Seo from "@/components/Seo";
import { RANGER_AVATAR, SUB_TOPICS, SUB_GENRES, SUB_INTERESTS, FREQ_OPTIONS } from "@/lib/constants";
import { toast } from "sonner";

const TABS = [
  { id: "alerts", label: "Alerts", icon: Bell },
  { id: "prefs", label: "Preferences", icon: SlidersHorizontal },
  { id: "follows", label: "Following", icon: Store },
  { id: "favorites", label: "Favorites", icon: Heart },
  { id: "events", label: "My Events", icon: Calendar },
  { id: "activity", label: "My Activity", icon: Star },
];

function FreqRow({ label, desc, icon: Ic, current, onChange }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-white/10 bg-[#141414] p-4">
      <div className="flex items-center gap-3">
        {Ic && <Ic size={18} className="text-[#00E5FF] flex-shrink-0" />}
        <div>
          <div className="text-white text-sm font-medium">{label}</div>
          {desc && <div className="text-neutral-500 text-xs">{desc}</div>}
        </div>
      </div>
      <select value={current} onChange={(e) => onChange(e.target.value)} data-testid={`freq-${label}`}
        className="bg-[#0f0f0f] border border-white/15 text-sm text-white px-3 py-2 focus:outline-none focus:border-[#00E5FF]">
        {FREQ_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

export default function CustomerDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("alerts");
  const [subs, setSubs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [favs, setFavs] = useState([]);
  const [events, setEvents] = useState([]);
  const [activity, setActivity] = useState({ reviews: [], contest: [] });

  useEffect(() => {
    if (!loading && !user) nav("/account/login");
    else if (!loading && user && user.role !== "customer") nav("/");
  }, [user, loading, nav]);

  const loadSubs = () => api.get("/me/subscriptions").then((r) => setSubs(r.data));
  const loadAlerts = () => api.get("/me/alerts").then((r) => setAlerts(r.data.alerts));
  useEffect(() => {
    if (!user || user.role !== "customer") return;
    loadSubs(); loadAlerts();
    api.get("/me/favorites").then((r) => setFavs(r.data));
    api.get("/me/events").then((r) => setEvents(r.data));
    api.get("/me/activity").then((r) => setActivity(r.data));
  }, [user]);

  useEffect(() => { if (tab === "alerts" && user?.role === "customer") api.post("/me/alerts/read").then(loadAlerts); }, [tab]);

  if (loading || !user) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-neutral-500">Loading...</div>;

  const freqFor = (kind, key) => (subs.find((s) => s.kind === kind && s.key === key)?.frequency) || "off";
  const setFreq = async (kind, key, label, frequency) => {
    if (frequency === "off") {
      const m = subs.find((s) => s.kind === kind && s.key === key);
      if (m) await api.delete(`/me/subscriptions/${m.id}`);
    } else {
      await api.post("/me/subscriptions", { kind, key, label, frequency });
    }
    loadSubs();
    toast.success("Preferences updated");
  };

  const follows = subs.filter((s) => s.kind === "venue" || s.kind === "operator");
  const unfollow = async (id) => { await api.delete(`/me/subscriptions/${id}`); loadSubs(); toast("Unfollowed"); };
  const unfav = async (slug) => { await api.post(`/me/favorites/${slug}`); api.get("/me/favorites").then((r) => setFavs(r.data)); toast("Removed"); };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter((e) => !e.date || e.date >= today);
  const past = events.filter((e) => e.date && e.date < today);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Seo title="My RiNo" description="Your Visit RiNo account." />
      <header className="glass border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={RANGER_AVATAR} alt="" className="w-8 h-8 object-cover border border-[#FF0055]" />
            <span className="font-display font-extrabold text-white">VISIT<span className="text-[#FF0055]">RiNo</span> <span className="text-neutral-500 text-sm font-normal">My RiNo</span></span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-neutral-400 text-sm hidden sm:block">{user.name || user.email}</span>
            <button onClick={() => { logout(); nav("/"); }} data-testid="customer-logout" className="text-neutral-400 hover:text-[#FF0055] flex items-center gap-1 text-sm"><LogOut size={16} /> Sign out</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex gap-1 mb-8 border-b border-white/10 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`account-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === t.id ? "border-[#FF0055] text-white" : "border-transparent text-neutral-400 hover:text-white"}`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "alerts" && (
          <div data-testid="alerts-feed">
            <h2 className="font-display font-bold text-2xl text-white mb-5">Your Alerts</h2>
            {alerts.length === 0 ? <div className="text-neutral-500 border border-white/10 py-12 text-center">No alerts yet. Follow venues and pick topics to start getting updates.</div>
              : <div className="space-y-2">{alerts.map((a) => (
                  <Link to={a.link || "#"} key={a.id} className="block border border-white/10 bg-[#141414] p-4 hover:border-[#00E5FF] transition-colors" data-testid={`alert-${a.id}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-[10px] font-accent uppercase px-2 py-0.5 ${a.type === "deal" ? "bg-[#FFEA00] text-black" : "bg-[#00E5FF] text-black"}`}>{a.type}</span>
                      <span className="text-neutral-600 text-xs">{new Date(a.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="text-white text-sm font-medium mt-2">{a.title}</div>
                    {a.body && <div className="text-neutral-400 text-sm mt-1">{a.body}</div>}
                  </Link>
                ))}</div>}
          </div>
        )}

        {tab === "prefs" && (
          <div className="space-y-8 max-w-2xl" data-testid="prefs-panel">
            <div>
              <h2 className="font-display font-bold text-2xl text-white mb-1">Newsletters & Topics</h2>
              <p className="text-neutral-500 text-sm mb-4">Choose what you get and how often. Set to Off anytime.</p>
              <div className="space-y-2">
                {SUB_TOPICS.map((t) => <FreqRow key={t.key} label={t.label} desc={t.desc} current={freqFor("topic", t.key)} onChange={(v) => setFreq("topic", t.key, t.label, v)} />)}
              </div>
            </div>
            <div>
              <h2 className="font-display font-bold text-2xl text-white mb-1 flex items-center gap-2"><Music size={20} className="text-[#00E5FF]" /> Music & Live</h2>
              <p className="text-neutral-500 text-sm mb-4">Get alerts when shows match your taste.</p>
              <div className="space-y-2">
                {SUB_GENRES.map((g) => <FreqRow key={g.key} label={g.label} current={freqFor("genre", g.key)} onChange={(v) => setFreq("genre", g.key, g.label, v)} />)}
              </div>
            </div>
            <div>
              <h2 className="font-display font-bold text-2xl text-white mb-1 flex items-center gap-2"><Wine size={20} className="text-[#FF0055]" /> Interests</h2>
              <p className="text-neutral-500 text-sm mb-4">Wine, beer, coffee and more — only what you care about.</p>
              <div className="space-y-2">
                {SUB_INTERESTS.map((i) => <FreqRow key={i.key} label={i.label} current={freqFor("interest", i.key)} onChange={(v) => setFreq("interest", i.key, i.label, v)} />)}
              </div>
            </div>
          </div>
        )}

        {tab === "follows" && (
          <div data-testid="follows-panel">
            <h2 className="font-display font-bold text-2xl text-white mb-5">Following <span className="text-neutral-500 text-base">({follows.length})</span></h2>
            {follows.length === 0 ? <div className="text-neutral-500 border border-white/10 py-12 text-center">You're not following anyone yet. Visit a venue or operator page and tap Follow.</div>
              : <div className="space-y-2">{follows.map((s) => (
                  <div key={s.id} className="flex items-center justify-between border border-white/10 bg-[#141414] p-4" data-testid={`following-${s.id}`}>
                    <div className="flex items-center gap-3">
                      {s.kind === "operator" ? <Compass size={18} className="text-[#00E5FF]" /> : <Store size={18} className="text-[#00E5FF]" />}
                      <Link to={s.kind === "operator" ? `/operators/${s.key}` : `/directory/${s.key}`} className="text-white text-sm hover:text-[#00E5FF]">{s.label}</Link>
                    </div>
                    <div className="flex items-center gap-3">
                      <select value={s.frequency} onChange={async (e) => { await api.post("/me/subscriptions", { kind: s.kind, key: s.key, label: s.label, frequency: e.target.value }); loadSubs(); }} className="bg-[#0f0f0f] border border-white/15 text-xs text-white px-2 py-1.5">
                        {FREQ_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <button onClick={() => unfollow(s.id)} data-testid={`unfollow-${s.id}`} className="text-neutral-500 hover:text-[#FF0055]"><Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}</div>}
          </div>
        )}

        {tab === "favorites" && (
          <div data-testid="favorites-panel">
            <h2 className="font-display font-bold text-2xl text-white mb-5">Favorite Places <span className="text-neutral-500 text-base">({favs.length})</span></h2>
            {favs.length === 0 ? <div className="text-neutral-500 border border-white/10 py-12 text-center">No favorites yet. Tap the heart on any venue to save it here.</div>
              : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{favs.map((b) => (
                  <div key={b.slug} className="border border-white/10 bg-[#141414] group" data-testid={`fav-${b.slug}`}>
                    <Link to={`/directory/${b.slug}`} className="block h-32 overflow-hidden"><img src={b.image} alt={b.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></Link>
                    <div className="p-3 flex items-center justify-between">
                      <Link to={`/directory/${b.slug}`} className="text-white text-sm font-medium truncate hover:text-[#00E5FF]">{b.name}</Link>
                      <button onClick={() => unfav(b.slug)} className="text-[#FF0055] flex-shrink-0"><Heart size={16} fill="#FF0055" /></button>
                    </div>
                  </div>
                ))}</div>}
          </div>
        )}

        {tab === "events" && (
          <div data-testid="my-events-panel" className="space-y-8">
            <div>
              <h2 className="font-display font-bold text-2xl text-white mb-4">Upcoming <span className="text-neutral-500 text-base">({upcoming.length})</span></h2>
              {upcoming.length === 0 ? <div className="text-neutral-500 border border-white/10 py-10 text-center">Nothing planned. Browse <Link to="/events" className="text-[#FF0055]">events</Link> and mark the ones you want to hit.</div>
                : <div className="space-y-2">{upcoming.map((e) => <MyEventRow key={e.slug} e={e} />)}</div>}
            </div>
            {past.length > 0 && (
              <div>
                <h2 className="font-display font-bold text-2xl text-white mb-4">Been there</h2>
                <div className="space-y-2 opacity-70">{past.map((e) => <MyEventRow key={e.slug} e={e} />)}</div>
              </div>
            )}
          </div>
        )}

        {tab === "activity" && (
          <div data-testid="activity-panel" className="space-y-10">
            <div>
              <h2 className="font-display font-bold text-2xl text-white mb-4">My Reviews <span className="text-neutral-500 text-base">({activity.reviews.length})</span></h2>
              {activity.reviews.length === 0 ? <div className="text-neutral-500 border border-white/10 py-10 text-center">No reviews yet.</div>
                : <div className="space-y-2">{activity.reviews.map((r) => (
                    <div key={r.id} className="border border-white/10 bg-[#141414] p-4" data-testid={`myreview-${r.id}`}>
                      <div className="flex items-center gap-2"><span className="text-[#FFEA00] text-sm">{"★".repeat(r.rating)}</span><Link to={`/directory/${r.business_slug}`} className="text-neutral-400 text-xs hover:text-white">{r.business_name || r.business_slug}</Link></div>
                      <p className="text-neutral-300 text-sm mt-1">{r.text}</p>
                    </div>
                  ))}</div>}
            </div>
            <div>
              <h2 className="font-display font-bold text-2xl text-white mb-4 flex items-center gap-2"><Camera size={20} className="text-[#00E5FF]" /> My Contest Photos <span className="text-neutral-500 text-base">({activity.contest.length})</span></h2>
              {activity.contest.length === 0 ? <div className="text-neutral-500 border border-white/10 py-10 text-center">You haven't entered the <Link to="/contest" className="text-[#FF0055]">photo contest</Link> yet.</div>
                : <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{activity.contest.map((c) => (
                    <div key={c.id} className="border border-white/10 bg-[#141414]" data-testid={`mycontest-${c.id}`}>
                      <img src={`${API}/images/${c.image_id}/file`} alt={c.caption} className="w-full h-32 object-cover" />
                      <div className="p-2 text-neutral-400 text-xs truncate">{c.caption || c.spot} · ♥ {c.likes}</div>
                    </div>
                  ))}</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MyEventRow({ e }) {
  const [status, setStatus] = useState(e.my_status);
  const opts = [["interested", "Interested"], ["going", "Going"], ["went", "Went"], ["none", "Remove"]];
  const change = async (v) => {
    await api.post(`/me/events/${e.slug}`, { status: v });
    setStatus(v === "none" ? null : v);
    toast.success(v === "none" ? "Removed" : "Updated");
  };
  if (status === null) return null;
  return (
    <div className="flex items-center justify-between border border-white/10 bg-[#141414] p-4" data-testid={`myevent-${e.slug}`}>
      <Link to={`/events/${e.slug}`} className="min-w-0">
        <div className="text-white text-sm font-medium truncate hover:text-[#00E5FF]">{e.title}</div>
        <div className="text-neutral-500 text-xs">{e.date} · {e.venue}</div>
      </Link>
      <select value={status} onChange={(ev) => change(ev.target.value)} data-testid={`myevent-status-${e.slug}`}
        className="bg-[#0f0f0f] border border-white/15 text-xs text-white px-2 py-1.5 flex-shrink-0">
        {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
